import React, { useState, useEffect } from "react";
import GreenButton from "../../../components/GreenButton/GreenButton";
import { useMediaQuery } from "@mui/material";
import BlueButton from "../../../components/BlueButton/BlueButton";
import { DataGrid } from "@mui/x-data-grid";
import CountriesSwipeableModals from "./CountriesSwipeableModals";
import MessageModals from "../../../components/MessageModals/MessageModals";
import DeleteUser from "./../../../assets/delete.svg";
import EditUser from "./../../../assets/edit-user.svg";
import AddGif from "./../../../assets/add-gif.gif";
import api from "../../../utils/api";

import {
  CountryColumns,
  GovernorateColumns,
  RegionColumns,
  ShippingColumns,
} from "../../../SourceData";

const CountriesCitiescitiess = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [view, setView] = useState("Country");
  const [isOpen, setIsOpen] = useState(false);
  const [currentModalView, setCurrentModalView] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteRoute, setDeleteRoute] = useState("");
  const [deleteText, setDeleteText] = useState("");

  // Pagination states
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getData();
  }, [view, paginationModel.page, paginationModel.pageSize]);

  const getData = async (viewToSet = null) => {
    // If a specific view was passed (for modal navigation), set it
    if (viewToSet) {
      setCurrentModalView(viewToSet);
      setIsOpen(true);
      return;
    }

    setLoading(true);
    try {
      let endpoint = "";
      switch (view) {
        case "Country":
          endpoint = "/countries";
          break;
        case "Governorate":
          endpoint = "/Governorates";
          break;
        case "Region":
          endpoint = "/cities";
          break;
        case "Shipping":
          endpoint = "/shipping";
          break;
        default:
          endpoint = "/countries";
      }

      // Add pagination parameters to the API call
      const params = {
        page: paginationModel.page + 1, // Convert to 1-based indexing for API
        limit: paginationModel.pageSize,
      };

      const res = await api.get(endpoint, { params });

      // Update data and pagination info from server response
      setData(res.data.data);
      setTotalRows(res.data.total);
      setTotalPages(res.data.totalPages);
      setCurrentPage(res.data.page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination model change
  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  // Reset pagination when view changes
  const handleViewChange = (newView) => {
    setView(newView);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setTotalPages(0);
    setCurrentPage(1);
  };

  const handleOpen = (view, item = null) => {
    setSelectedItem(item);
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = (item, type) => {
    setItemToDelete(item);

    if (type === "bank") {
      setDeleteRoute(`/banks/${item.id}`);
      setDeleteText("تم حذف البنك بنجاح");
    } else {
      const endpointMap = {
        Country: "countries",
        Governorate: "Governorates",
        Region: "cities",
        Shipping: "shipping",
      };
      const nameMap = {
        Country: "البلد",
        Governorate: "المحافظة",
        Region: "المنطقة",
        Shipping: "سعر الشحن",
      };
      setDeleteRoute(`/${endpointMap[view] || "countries"}/${item.id}`);
      setDeleteText(`تم حذف ${nameMap[view] || "العنصر"} بنجاح`);
    }

    setOpenDelete(true);
  };

  // Refresh data after delete and adjust pagination if needed
  const handleDataRefresh = async () => {
    // If we're on a page that no longer exists after deletion, go to the last valid page
    const maxPage = Math.max(
      0,
      Math.ceil((totalRows - 1) / paginationModel.pageSize) - 1
    );
    if (paginationModel.page > maxPage) {
      setPaginationModel((prev) => ({ ...prev, page: maxPage }));
    } else {
      await getData();
    }
  };

  const actions = {
    field: "action",
    headerName: "الإجراءات",
    width: 320,
    headerAlign: "center",
    align: "center",
    sortable: false,
    renderCell: (params) => (
      <div className="cell-action">
        {view === "Country" ? (
          <>
            <BlueButton
              text="البنوك"
              size={isMobile ? "small" : isTablet ? "fit" : "medium"}
              variant="fill"
              onClick={() => handleOpen("viewBank", params.row)}
            />
            <div
              className="cell-btn"
              onClick={() => handleOpen(`edit${view}`, params.row)}
            >
              <img src={EditUser} alt="" />
            </div>
            <div
              className="cell-btn"
              onClick={() => handleDeleteItem(params.row)}
            >
              <img src={DeleteUser} alt="" />
            </div>
          </>
        ) : (
          <>
            <div
              className="cell-btn"
              onClick={() => handleOpen(`edit${view}`, params.row)}
            >
              <img src={EditUser} alt="" />
            </div>
            <div
              className="cell-btn"
              onClick={() => handleDeleteItem(params.row)}
            >
              <img src={DeleteUser} alt="" />
            </div>
          </>
        )}
      </div>
    ),
  };

  const getColumns = () => {
    switch (view) {
      case "Country":
        return CountryColumns.concat(actions);
      case "Governorate":
        return GovernorateColumns.concat(actions);
      case "Region":
        return RegionColumns.concat(actions);
      case "Shipping":
        return ShippingColumns.concat(actions);
      default:
        return CountryColumns.concat(actions);
    }
  };

  const getAddButtonText = () => {
    switch (view) {
      case "Country":
        return "اضافة بلد جديد";
      case "Governorate":
        return "اضافة محافظة جديد";
      case "Region":
        return "اضافة منطقة جديد";
      case "Shipping":
        return "اضافة سعر شحن جديد";
      default:
        return "اضافة";
    }
  };

  return (
    <div className="departments-container">
      <div className="actions-container">
        <div className="btns-container">
          <GreenButton
            text="البلاد"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={view === "Country" ? "fill" : "outlined"}
            onClick={() => handleViewChange("Country")}
          />
          <GreenButton
            text="المحافظات"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={view === "Governorate" ? "fill" : "outlined"}
            onClick={() => handleViewChange("Governorate")}
          />
          <GreenButton
            text="المناطق"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={view === "Region" ? "fill" : "outlined"}
            onClick={() => handleViewChange("Region")}
          />
          <GreenButton
            text="اسعار الشحن"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={view === "Shipping" ? "fill" : "outlined"}
            onClick={() => handleViewChange("Shipping")}
          />
        </div>

        <BlueButton
          text={getAddButtonText()}
          size={isMobile ? "full" : isTablet ? "fit" : "large"}
          variant="fill"
          onClick={() => handleOpen(`create${view}`)}
          icon={<img src={AddGif} alt="" />}
        />
      </div>

      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={getColumns()}
        rows={data.map((row, index) => ({
          ...row,
          index: paginationModel.page * paginationModel.pageSize + index + 1,
        }))}
        // Pagination props
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={totalRows}
        pageSizeOptions={[5, 10, 25, 50]}
        // Other props
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        rowHeight={60}
        loading={loading}
      />

      <CountriesSwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={() => setIsOpen(true)}
        currentView={currentModalView}
        initialData={selectedItem}
        refreshData={getData}
      />

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={deleteRoute || `/${view}/${itemToDelete?.id}`}
        text={
          deleteText ||
          `تم حذف ${
            view === "Country"
              ? "البلد"
              : view === "Governorate"
              ? "المحافظة"
              : "المنطقة"
          } بنجاح`
        }
        onClose={() => setOpenDelete(false)}
        onDataChange={handleDataRefresh}
      />
    </div>
  );
};

export default CountriesCitiescitiess;
